import * as types from 'constants/ActionTypes';
import store from '@/store';
import { omit, intersectionBy, unionBy } from 'lodash';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import { change as changeFormField, reset as resetForm } from 'redux-form';
import { getSessionId, triggerSignOutWidget } from 'utils/session';
// eslint-disable-next-line import/no-unresolved
import { trip, purchase as purchaseApi } from 'js-api-client';
import { decodePassengers, purchaseTotalByCategory } from 'utils/Reserbus';
import { getExpirationTime } from 'utils/purchase/getExpirationTime';
import { getSeatPriceZonesFromSource } from 'utils/seatPricing';
import camelizeConverter from 'utils/camelizeConverter';
import checkIfRetriesExceeded from 'utils/polls/checkIfRetriesExceeded';
import pollingPromises from 'utils/pollingPromises';
import shouldAlignPaymentEngineWithSelection from 'utils/purchase/shouldAlignPaymentEngineWithSelection';
import parsePurchase from 'models/parsePurchase';
import { normalizeVivaPassengers } from 'utils/purchaseResponseTransform';
import isSetSeats from 'utils/purchase/validateSetSeat';
import Cookies from 'js-cookie';
import getSeats from 'utils/purchase/getSeats';
import normalizeRegex from 'utils/normalizeRegex';
import { formatName } from 'utils/purchase/parsePassenger';
import yuno from 'payments/core/engines/yuno';
import { isFrequentsPassengerEnabled } from 'utils/userPreferences';
import isCdsFrequentPassengerEnabled from 'utils/isCdsFrequentPassengerEnabled';
import isCdsFrequentPassengerBootstrapConfirmedAnonymous from 'utils/isCdsFrequentPassengerBootstrapConfirmedAnonymous';
import transformProductsToAncillaries from 'utils/productsCart/transformProductsToAncillaries';
import { selectWhitelabelConfig } from 'selectors/whitelabelSelectors';
import { selectPurchase } from 'selectors/purchaseSelectors';
import { selectSelectedProducts } from 'selectors/productsCartSelectors';
import { isNativeAppWebView } from 'utils/webview';
import { EXCHANGE_HOME_PATH, getExchangeLockScheduleRedirectPath } from 'utils/exchange';
import {
  trackApiError,
  trackLockSeatsError,
  trackBusSchemeError,
} from 'metrics/user-analytics/errorTracker';
import {
  getRnMixpanelBridgedTrackerId,
  getRnMixpanelNativePurchasePayloadProps,
} from 'utils/rnMixpanelNativeProps';
import { handleProductUnavailable } from './productsCart';
import {
  getUserByWalletType,
  getWalletTypeForPurchase,
  setUserTokenForAPI,
} from '../utils/loyalty';
import { setError } from '.';
import { setTerminals, setAirports, setLines, setCarriers } from './catalogs';
import customServerError from '../utils/purchase/customServerError';
import { getBrand2ProcessorMap } from '../utils/documentMaps';
import wayIsOpenTicket from '../utils/wayIsOpenTicket';
// eslint-disable-next-line import/no-cycle
import { setCostaPassError } from './costapass';
import { getInitialPassengers, reorderPassengers } from '../utils/passengers';
import userFingerprint from '../services/userFingerprint';
import { setSeatsBusError } from './seatsBus';
import { setUpFrequentPassengers } from './userPreferences';
import {
  isPassengerEmailValidationError,
  isPurchaserEmailValidationError,
  handleEmailValidationError,
  isProductUnavailableError,
  extractUnavailableProductCodes,
} from '../utils/purchase/parseBackendErrors';
import { getCurrentPnr } from '../selectors/viajeAhorrandoSelector';
import { errorTripExchange } from './exchange';
import { serializeCarouselSession } from '../metrics/user-analytics/purchase';

/**
 * Strip UI-only meta keys from a passenger object before shipping it to the BE.
 * These keys (e.g., `lapChildExpanded`) are session-scoped form state and must
 * not appear in /purchases or /passengers payloads, where they would confuse
 * the backend contract.
 * @param {Object} passenger - Passenger object with optional `meta` field.
 * @returns {Object} Same passenger, with UI-only meta keys removed.
 */
const stripUiOnlyPassengerMeta = (passenger) => {
  if (!passenger?.meta) return passenger;

  return { ...passenger, meta: omit(passenger.meta, ['lapChildExpanded']) };
};

/**
 * Polls until next departures for bulk tickets are ready
 * @param {string} token - Purchase token
 * @returns {Function} Thunk action creator
 */
export function pollNextDepartures(token) {
  return (dispatch) => {
    dispatch({ type: types.REQUEST_NEXT_DEPARTURES });

    const polls = pollingPromises({
      data: [token],
      name: 'onReceiveNextDepartures',
      create: (polling, token) => purchaseApi.getNextDepartures(token, polling),
      finishedStatus: ['completed'],
      failedStatus: ({ status }) => !['pending', 'completed'].includes(status),
    });

    return Promise.all(polls).then((polls) => {
      polls.forEach(({ status }) => {
        checkIfRetriesExceeded(status);
      });

      const nextDepartures = polls[0].payload;

      dispatch({
        type: types.RECEIVE_NEXT_DEPARTURES,
        nextDepartures,
        receivedAt: Date.now(),
      });
    });
  };
}

export function selectInstallmentsPlan(installmentsPlan) {
  return { type: types.SELECT_MONTHLY_PLAN, installmentsPlan };
}

export function selectInstalmentsPlan(selectedPlan) {
  return { type: types.SET_SELECTED_PLAN, selectedPlan };
}

export function resetInstallmentsPlan() {
  return {
    type: types.SELECT_MONTHLY_PLAN,
    installmentsPlan: { card: '', months: 1, total: 0, monthlyPayment: 0 },
  };
}

/**
 * Sets the enable monthly selected plan state based on installments validation
 * @param {boolean} enableMonthlySelectedPlan - Whether monthly installments are enabled
 * @returns {Object} Action to set enable monthly selected plan
 */
export function setEnableMonthlySelectedPlan(enableMonthlySelectedPlan) {
  return {
    type: types.SET_ENABLE_MONTHLY_SELECTED_PLAN,
    enableMonthlySelectedPlan,
  };
}

const getAvailableBusCategories = (departs, returns = {}) => {
  let dPassengerTypes = [];
  let rPassengerTypes = [];
  let departureHasBus = false;
  let returnHasBus = false;
  let isDepartureOpenTicket = false;
  let isReturnOpenTicket = false;

  departs.oneWayPurchases.forEach(({ trip }) => {
    const { transportType, openTicket, passengerTypes = [] } = trip;
    const availableTypes = passengerTypes.filter(({ availability }) => availability > 0);

    if (transportType === 'bus' && dPassengerTypes.length === 0) {
      departureHasBus = true;
      dPassengerTypes = availableTypes;
      isDepartureOpenTicket = !!openTicket;
    } else if (transportType === 'bus') {
      dPassengerTypes = intersectionBy(dPassengerTypes, availableTypes, 'type');
    }
  });

  if (returns.oneWayPurchases) {
    returns.oneWayPurchases.forEach(({ trip }) => {
      const { transportType, openTicket, passengerTypes = [] } = trip;
      const availableTypes = passengerTypes.filter(({ availability }) => availability > 0);

      if (transportType === 'bus' && rPassengerTypes.length === 0) {
        returnHasBus = true;
        rPassengerTypes = availableTypes;
        isReturnOpenTicket = !!openTicket;
      } else if (transportType === 'bus') {
        rPassengerTypes = intersectionBy(rPassengerTypes, availableTypes, 'type');
      }
    });
  }

  if (departureHasBus && returnHasBus) {
    const { JUST_ADULT_OPEN_TICKET } = store.getState().whitelabelConfig.features;

    if (JUST_ADULT_OPEN_TICKET) {
      if (isDepartureOpenTicket && !isReturnOpenTicket) {
        return rPassengerTypes;
      }

      if (isReturnOpenTicket && !isDepartureOpenTicket) {
        return dPassengerTypes;
      }
    }

    return intersectionBy(dPassengerTypes, rPassengerTypes, 'type');
  }

  return unionBy(dPassengerTypes, rPassengerTypes, 'type');
};

const isPlanAvailable = (qualifiesForInstallments, selectedInstallmentsPlan, paymentPlans) => {
  if (qualifiesForInstallments) {
    const { card, months } = selectedInstallmentsPlan;
    return Boolean(card && paymentPlans[card][months]);
  }

  return false;
};

export function requestPayment() {
  return { type: types.REQUEST_PAYMENT };
}

export function resetPaymentCardError() {
  return { type: types.RESET_PAYMENT_CARD_ERROR };
}

export function resetPayment() {
  return { type: types.RESET_PAYMENT };
}

export function requestPurchase() {
  return { type: types.REQUEST_PURCHASE };
}

export function receivePayment(payment = {}) {
  return (dispatch) => {
    dispatch({
      type: types.RECEIVE_PAYMENT,
      receivedAt: Date.now(),
      payload: camelizeKeys(payment),
    });
  };
}

export function receivePurchase(purchaseResponse) {
  return (dispatch, getState) => {
    const { features } = getState().whitelabelConfig;
    const tenantSupportsConnections = features.SUPPORT_TRIPS_WITH_CONNECTIONS;

    // Parse purchase data
    const parsedPurchase = parsePurchase(purchaseResponse, tenantSupportsConnections);

    // Normalize passengers only if vivaPnr exists in meta (Viaja Ahorrando is active)
    const hasVivaPnr = parsedPurchase?.meta?.vivaPnr;
    if (hasVivaPnr && parsedPurchase?.passengers && parsedPurchase.passengers.length > 0) {
      parsedPurchase.passengers = normalizeVivaPassengers(parsedPurchase.passengers);
    }

    dispatch({
      type: types.RECEIVE_PURCHASE,
      purchase: parsedPurchase,
      tenantSupportsConnections,
      receivedAt: Date.now(),
    });
  };
}

/**
 * AM-1686: fetches the purchase and updates only the `storeReferenceIssued` flag.
 * Used after a store payment attempt so the QuickCheckout edit guard reflects the
 * just-issued reference. Kept intentionally lightweight (no full receivePurchase) to avoid
 * disrupting the payment-failed state or triggering a heavy re-render.
 *
 * @param {string} token - Purchase token.
 * @returns {Function} Thunk that dispatches SET_STORE_REFERENCE_ISSUED.
 */
export function refreshStoreReferenceIssued(token) {
  return (dispatch) =>
    purchaseApi
      .get(token)
      .then((response) => {
        const value = Boolean(response?.purchase?.store_reference_issued);
        dispatch({ type: types.SET_STORE_REFERENCE_ISSUED, value });
        return value;
      })
      .catch(() => false);
}

const setFailedStatePurchaseAction = {
  type: types.SET_FAILED_STATE_PURCHASE,
};

export function resetPurchase() {
  return { type: types.RESET_PURCHASE };
}

export function updatePurchase(updating) {
  return { type: types.UPDATE_PURCHASE, updating };
}

export function setBusCategories(busCategories, tripSlug = null) {
  return {
    type: types.SET_BUS_CATEGORIES,
    busCategories,
    tripSlug,
  };
}

/**
 * Sets the seat map for a given trip.
 * @param {string} way - The direction of the trip (e.g., 'departure', 'return').
 * @param {Object} trip - The trip object containing bus and diagramType.
 * @param {string} tripSlug - The unique identifier for the trip.
 * @param {string} [saveIn='purchase'] - The context in which the seat map is being saved.
 * @param {Array} busCategories - Categories of buses available for the trip.
 * @returns {Object} The action object with type and payload for setting the seat map.
 */
export function setSeatMap(
  way,
  trip,
  tripSlug,
  saveIn = 'purchase',
  busCategories,
  layouts,
  seats,
  trips,
) {
  const seatPriceZones = getSeatPriceZonesFromSource(trip);
  const tripDetails = trip.trip || trip;
  const { bus: tripBus, diagramType } = trip;
  const passengerTypes =
    (Array.isArray(tripDetails.passengerTypes) && tripDetails.passengerTypes.length > 0
      ? tripDetails.passengerTypes
      : trip.passengerTypes) || [];
  const tripPricingFields = ['pricing', 'departureRoundTripPricing', 'roundTripPricing'].reduce(
    (pricingFields, pricingKey) => {
      const value = tripDetails[pricingKey] ?? trip[pricingKey];
      return value == null ? pricingFields : { ...pricingFields, [pricingKey]: value };
    },
    {},
  );
  const layoutsValues = layouts ? Object.values(layouts) : [];
  /**
   * When a trip has connections, the fetchTripDetails response has a null value for the bus key (tripBus),
   * So if the trips has connections, we take the bus from the first layout at layoutsValues.
   * This first value is the bus layout for the first connection.
   * When a trip has connections the layout attribute is not useful, but the code dies if this value is not set.
   */
  const bus = layoutsValues.length ? layoutsValues[0] : tripBus;
  return {
    way,
    layout: bus,
    diagramType,
    type: saveIn === 'purchase' ? types.SET_SEAT_MAP : types.SET_SEAT_MAP_TRIPS,
    tripSlug,
    busCategories,
    layouts,
    seats,
    trips,
    seatPriceZones,
    tripPricingFields,
    passengerTypes,
  };
}

export function requestTripsDetails(way) {
  return { type: types.REQUEST_TRIPS_DETAILS, way };
}

export function receiveTripsDetails(way, tripSlug) {
  return { type: types.RECEIVE_TRIPS_DETAILS, way, tripSlug };
}

export function postPassengers() {
  return { type: types.PURCHASE_POST_PASSENGERS };
}

/**
 * Sets the passengers for the purchase
 * @param {Array} passengers - List of passengers
 * @param {string} token - Purchase token
 * @returns {Object} Action to set passengers
 */
export function setPassengers(passengers, token) {
  const { purchase } = store.getState();
  const { isExchange, bulkTicket } = purchase.toJS();

  /**
   * Exchange and bulk ticket purchases require to reorder the passengers
   * As the passengers are sent in the same order as they are created, we need to reorder them
   * to avoid minor being as first passenger
   */
  if (isExchange || bulkTicket) {
    passengers = reorderPassengers(passengers);
  }

  // Normalize Viva passengers before camelizing (normalization works with snake_case format)
  // Function validates internally if Viaja Ahorrando is active
  const normalizedPassengers = normalizeVivaPassengers(passengers);

  return {
    type: types.PURCHASE_SET_PASSENGERS,
    passengers: camelizeKeys(normalizedPassengers),
    token,
  };
}

export function setPaymentPlans(paymentPlans) {
  return {
    type: types.PURCHASE_SET_PAYMENT_PLANS,
    paymentPlans,
  };
}

export function receiveWalletType(walletType, flatFareAvailable) {
  return {
    type: types.RECEIVE_WALLET_TYPE,
    walletType,
    flatFareAvailable,
  };
}

/**
 * @typedef {'credit_card' | 'paypal' | 'coppel_pay' | 'oxxo' | 'efecty' | 'transfer' | 'nequi' | 'bnpl'} PaymentOption
 */

/**
 * Handles the payment option selection, this value represent the selected payment option
 * @param {PaymentOption} option - Payment option
 * @returns {Object} Event dispatch
 */
export function selectPaymentOption(option) {
  return { type: types.SELECT_PAYMENT_OPTION, option };
}

/**
 * @typedef {Object} PaymentMethod
 * @property {string} type - Payment method type
 * @property {string} engine - Payment method engine
 * @property {string} provider - Payment method provider
 */

/**
 * Function that handles the selection of a payment method
 * This payment method is a new alternative with more information about the payment method
 * @param {PaymentMethod} method - Payment method
 * @returns {Object} Event dispatch
 */
export function selectPaymentMethod(method) {
  return { type: types.SELECT_PAYMENT_METHOD, method };
}

/**
 * Function that handles the creation of a new purchase
 * @param {String} dSlug - Departure slug
 * @param {String} rSlug - Return slug
 * @param {String} passengers - Passenger selection.
 * @param {String | Number} [seenPrice] - Seen price
 * @param {String} [adAttribution] - Ad attribution
 * @param {Boolean} [isExchange] - Is exchange
 * @param {Object} [exchangeData] - Exchange data
 * @param {Boolean} [redirect] - Redirect
 * @param {String} [redirectedFrom] - Redirected from brand
 * @param {Object} [tripInfo] - Trip info
 * @param {Object} [preSelectedSeats] - Preselected seat data.
 * @param {Boolean} [quickPurchase] - Whether purchase is for quick checkout.
 * @param {String} [externalCouponCode] - Explicit external coupon code.
 * @returns Event dispatch
 */
export function newPurchase(
  dSlug,
  rSlug,
  passengers = 'A1',
  seenPrice,
  adAttribution,
  isExchange = false,
  exchangeData = {},
  redirect = false,
  redirectedFrom = '',
  tripInfo,
  preSelectedSeats,
  quickPurchase = false,
  externalCouponCode,
) {
  return (dispatch) => {
    const state = store.getState();
    const {
      search,
      whitelabelConfig: { env },
      carouselSession,
    } = state;
    const decodedPassengers = decodePassengers(passengers);
    const externalCoupon = externalCouponCode ?? search.get('couponCode', null);
    const vivaPnr = getCurrentPnr(state);
    const { operationNumbers, nit, document, email } = exchangeData;
    const userFingerprintValue = userFingerprint.getFingerprint();

    const googleAnalyticsID = Cookies.get('_ga'); // Google Analytics ID to track offline purchases
    const rnNativePayloadProps = getRnMixpanelNativePurchasePayloadProps();
    const payload = {
      departs: dSlug,
      returns: rSlug,
      passenger_selection: decodedPassengers,
      seen_price: seenPrice && Number(seenPrice),
      ad_attribution: adAttribution === 'true',
      device_fingerprint: userFingerprintValue,
      tracker_id: getRnMixpanelBridgedTrackerId(),
      webview: isNativeAppWebView(),
      ...rnNativePayloadProps,
      ga_client_id: googleAnalyticsID,
      ...(env.riskified && env.riskified.enabled && { riskified_beacon_id: getSessionId() }),
      locale:
        typeof window !== 'undefined' && window.localStorage
          ? window.localStorage.getItem('i18nextLng') || 'es-MX'
          : 'es-MX',
      external_coupon_code: externalCoupon,
      ...(vivaPnr && { viva_pnr: vivaPnr }),
      ...(isExchange && {
        operation_number: operationNumbers,
        exchange_ticket_keys: operationNumbers,
        nit,
        document,
        email,
      }),
      redirected_from: redirectedFrom,
      redirect,
      ...(tripInfo && decamelizeKeys(tripInfo)),
      ...(preSelectedSeats && decamelizeKeys({ ...preSelectedSeats })),
      ...(quickPurchase && { quick_purchase: true }),
      ...(carouselSession && {
        days_carousel_track_props: decamelizeKeys(serializeCarouselSession(carouselSession)),
      }),
    };

    // The user token is set in the requests
    setUserTokenForAPI();
    if (isFrequentsPassengerEnabled()) {
      dispatch(setUpFrequentPassengers());
    }
    dispatch(resetPurchase());
    dispatch(requestPurchase());

    if (isExchange) {
      dispatch({ type: types.SET_EXCHANGE_OPERATION });
    }

    return purchaseApi
      .create(payload)
      .then((purchaseResponse) => {
        const {
          terminals,
          airports,
          lines,
          carriers,
          purchase: { departs, returns, token },
        } = camelizeKeys(purchaseResponse, camelizeConverter);
        const busCategories = getAvailableBusCategories(departs, returns);

        dispatch(setTerminals(terminals));
        dispatch(setAirports(airports));
        dispatch(setLines(lines));
        dispatch(setCarriers(carriers));
        dispatch(setBusCategories(busCategories));
        dispatch(receivePurchase(purchaseResponse));

        return token;
      })
      .catch((reason) => {
        const { code, message } = reason;
        const codeType = 200;
        if (!redirect) {
          // eslint-disable-next-line prettier/prettier
          dispatch(
            setError(
              codeType,
              'trip_not_available',
              'warning',
              true,
              customServerError(code, message),
              isExchange && EXCHANGE_HOME_PATH,
            ),
          );
        }
        dispatch(setFailedStatePurchaseAction);
        if (isExchange) dispatch(errorTripExchange(true));
        trackApiError({
          status: code ?? reason?.status,
          message: message ?? reason?.message,
          url: 'purchase.create',
          funnel_step: 'Purchase Attempt',
        });
        console.error(reason);
      });
  };
}

export function getPurchase(
  token,
  update = false,
  { preservePaymentOptionSelection = false } = {},
) {
  const {
    whitelabelConfig: {
      env: { defaultPaymentMethod, defaultPaymentOption },
    },
    payment,
  } = store.getState();

  return (dispatch) => {
    if (update) {
      dispatch(updatePurchase(true));
    } else {
      dispatch(requestPurchase());
    }

    return purchaseApi
      .get(token)
      .then((purchaseResponse) => {
        const {
          terminals,
          airports,
          lines,
          carriers,
          purchase: {
            departs,
            lastPaymentId,
            returns,
            paymentMethods,
            paymentEngine: purchasePaymentEngine,
          },
        } = camelizeKeys(purchaseResponse, camelizeConverter);

        const busCategories = getAvailableBusCategories(departs, returns);
        const defaultMatchingMethod =
          paymentMethods.find((method) => {
            return (
              method.type === defaultPaymentMethod.type &&
              method.engine === defaultPaymentMethod.engine &&
              method.provider === defaultPaymentMethod.provider
            );
          }) || {};

        // When refreshing an in-progress purchase (e.g. the relock after editing passengers
        // in Quick Checkout), keep the payment option/method the user already chose if it's
        // still offered, instead of resetting to the tenant default. Resetting would flip
        // selectedPaymentOption, remounting the keyed PaymentForm and wiping entered card data
        // mid-payment. Only opted-in callers (preservePaymentOptionSelection) get this.
        // selectedPaymentMethod is stored as a plain JS object, so read it with getIn instead
        // of serializing the whole purchase tree with toJS.
        const currentMethod = store.getState().purchase.getIn(['selectedPaymentMethod']);
        // Preserve the user's choice using the refreshed method object from the new response
        // (not the stale one from the old store state), so any backend metadata updates apply.
        const preservedMatchingMethod =
          preservePaymentOptionSelection && currentMethod
            ? paymentMethods.find(
                (method) =>
                  method.type === currentMethod.type &&
                  method.engine === currentMethod.engine &&
                  method.provider === currentMethod.provider,
              )
            : null;
        const currentMethodStillAvailable = Boolean(preservedMatchingMethod);
        const paymentMethod = preservedMatchingMethod || defaultMatchingMethod;

        dispatch(setTerminals(terminals));
        dispatch(setAirports(airports));
        dispatch(setLines(lines));
        dispatch(setCarriers(carriers));
        dispatch(setBusCategories(busCategories));
        dispatch(selectPaymentMethod(paymentMethod));
        // Skip the option reset when preserving: the receivePurchase reducer keeps the current
        // selectedPaymentOption, so dispatching the tenant default here would override it.
        if (!currentMethodStillAvailable) {
          dispatch(selectPaymentOption(defaultPaymentOption));
        }
        dispatch(receivePurchase(purchaseResponse));

        if (
          shouldAlignPaymentEngineWithSelection({
            selectedMethodEngine: paymentMethod.engine,
            purchasePaymentEngine,
            lastPaymentId,
          })
        ) {
          dispatch(selectPaymentMethod(paymentMethod));
        }

        const currentRoute = window.location.pathname;
        const { id: currrentLastPaymentId, status } = payment.toJS();
        if (
          status === 'failed' &&
          lastPaymentId === currrentLastPaymentId &&
          currentRoute.includes('checkout')
        )
          return;

        if (lastPaymentId) {
          dispatch(requestPayment());
          purchaseApi
            .getPayment(token, lastPaymentId)
            .then((paymentResponse) => {
              dispatch(receivePayment(paymentResponse));
            })
            .catch((reason) => {
              dispatch(setError(304, 'error_when_looking_for_payment', 'error', true));
              dispatch(receivePayment());
              throw new Error(`Error 304: Failed to get payment. ${reason.message}`);
            });
        }
      })
      .catch((reason) => {
        dispatch(setError(201, 'error_when_generating_purchase'));
        trackApiError({
          status: reason?.code || reason?.status,
          message: reason?.message,
          url: 'purchase.create',
        });
        console.error(reason);
      });
  };
}

/**
 * Fetches a purchase by token for the quick purchase flow and validates that the
 * expected purchase actually loaded into the store.
 *
 * @param {string} token - Purchase token to fetch.
 * @returns {Function} Thunk that resolves when the purchase loaded, or rejects with
 *   a `QUICK_PURCHASE_NOT_FOUND` error when the loaded purchase does not match `token`.
 */
export function fetchQuickPurchaseByToken(token) {
  return (dispatch, getState) => {
    dispatch(resetPurchase());
    return dispatch(getPurchase(token)).then(() => {
      const { purchase } = getState();
      const loadedToken = purchase.get('token');
      const purchaseLoaded = purchase.get('loaded');

      if (loadedToken !== token || !purchaseLoaded) {
        return Promise.reject(new Error('QUICK_PURCHASE_NOT_FOUND'));
      }

      return null;
    });
  };
}

export function unlockSeats({ purchaseToken, forceUnlock, shouldUpdateState, dispatch }) {
  const { pathname } = window.location;
  if (!forceUnlock && (pathname.includes('purchase') || pathname.includes('payment'))) return;

  if (shouldUpdateState) dispatch(updatePurchase(true));
  return purchaseApi
    .unlockSeats(purchaseToken)
    .then((purchaseResponse) => {
      if (shouldUpdateState && dispatch && purchaseResponse) {
        dispatch(receivePurchase(purchaseResponse));
      }
    })
    .catch((reason) => {
      console.error(reason);
    });
}

export function updatingWalletType(updating) {
  return {
    type: types.UPDATING_WALLET_TYPE,
    updating,
  };
}

export function updateWalletType({
  purchaseToken,
  walletType,
  needsUnlockSeats,
  needsDisableFlatFare,
}) {
  return (dispatch) => {
    const payload = {
      wallet_type: walletType,
      ...(needsDisableFlatFare && { wants_flat_fare: false }),
    };
    dispatch(updatingWalletType(true));
    purchaseApi
      .update(purchaseToken, payload)
      .then((response) => {
        const {
          purchase: { walletType, flatFareAvailable },
        } = camelizeKeys(response);
        dispatch(receiveWalletType(walletType, flatFareAvailable));
        if (needsUnlockSeats) {
          unlockSeats({ purchaseToken, forceUnlock: true, shouldUpdateState: true, dispatch });
        }
      })
      .catch((reason) => {
        dispatch(setError(202, reason.message, 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(updatingWalletType(false));
      });
  };
}

/**
 * Returns and object with the bus layouts by trip slug.
 * The resultant object would have the tripSlug for each seat.
 *
 * @param {*} trips
 * @returns {Object} - Object with the bus layouts by trip slug.
 *
 * @example
 * const layouts = getConnectionTripsBusLayouts(trips);
 * console.log(layouts);
 * // {
 * // "trip-slug1": [[ [ {category: "seat", tripSlug: "trip-slug1"}], [{categoty: "hallway"}] ]],
 * // "trip-slug2": [[ [], [] ]],
 * // }
 */
const getConnectionTripsBusLayouts = (trips) => {
  return trips.reduce((acc, trip) => {
    const {
      bus,
      trip: { id: slug },
    } = trip;
    /**
     * Adding the tripSlug to the seats object in the bus layout.
     * It doesn't modifies the bus object structure, it just adds the tripSlug to the seats.
     */
    bus
      .flat(2)
      .filter((item) => item.number)
      .forEach((item) => {
        item.tripSlug = slug;
      });
    acc[slug] = bus;
    return acc;
  }, {});
};

/**
 * Returns an object with the seats by trip slug.
 * The resultant object would have the tripSlug for each seat.
 *
 * @param {*} trips
 * @returns - Object with the bus seats by trip slug.
 *
 * @example
 * const seats = getConnectionTripsSeats(trips);
 * console.log(seats);
 * // {
 * // "trip-slug1": [{ number: "15", tripSlug: "trip-slug1" }, { number: "16", tripSlug: "trip-slug1" }],
 * // "trip-slug2": [{ number: "1", tripSlug: "trip-slug2" }, { number: "2", tripSlug: "trip-slug2" }],
 * // }
 */
const getConnectionTripsSeats = (trips) => {
  return trips.reduce((acc, trip) => {
    const {
      bus,
      trip: { id: slug },
    } = trip;
    acc[slug] = getSeats(bus, slug);
    return acc;
  }, {});
};

/**
 * Fetches detailed information about trips based on departure and return fragments.
 * When provided, purchaseToken is sent as purchase_token to the trip details service (e.g. for exchange flow).
 * @param {Object} params - The parameters for fetching trip details.
 * @param {Array} params.departureFragments - Array of departure trip fragments.
 * @param {Array} [params.returnFragments=[]] - Array of return trip fragments (optional).
 * @param {string} [params.purchaseToken] - Optional. In exchange flow, current purchase token; sent as purchase_token to the trip details service.
 * @param {string} [params.saveIn='purchase'] - Key in Redux state where fetched trip details are stored.
 */
export function fetchTripsDetails({
  departureFragments,
  returnFragments = [],
  saveIn = 'purchase',
  purchaseToken,
}) {
  return (dispatch) => {
    const seatsBus = store.getState().seatsBus.toJS();
    const { features } = store.getState().whitelabelConfig;

    /**
     * @todo Remove this feature flag
     * We can determine if the trip has connections using the trip fragments
     * so we don't really need this feature flag.
     */
    const tenantSupportsTripsWithConnections = features.SUPPORT_TRIPS_WITH_CONNECTIONS;
    const saveInPurchase = saveIn === 'purchase';

    let tripIds = [...departureFragments, ...returnFragments].map((owp) => owp.id);

    if (isSetSeats(seatsBus, tripIds) && !saveInPurchase) {
      return;
    }

    /**
     * When the trip has connections, the purchase response has more than one fragment (trip connections count + 1)
     * The +1 is the "general" trip, which has the primary origin and final destination.
     * The id from this trip is the one that we need to use in order to fetch the bus layouts of the connection trips.
     * This "general" trip always comes in the first position of the fragments array, so we're getting its ID with this code.
     * In that way, we only fetch details of the "general" trip.
     */
    if (tenantSupportsTripsWithConnections) {
      if (departureFragments.length) tripIds = [departureFragments[0].id];
      if (returnFragments.length) tripIds.push(returnFragments[0].id);
    }

    /**
     * Dispatching the action to indicate that the bus data is being refreshed.
     * This is useful to show a loading spinner while the data is being fetched.
     */
    dispatch(updatePurchase(true));

    const polls = pollingPromises({
      data: tripIds,
      name: 'onReceiveTrip',
      create: (polling, tripId) =>
        trip.getWithBus(
          tripId,
          { include: ['bus'], ...(purchaseToken && { purchase_token: purchaseToken }) },
          polling,
        ),
    });
    if (saveInPurchase) {
      dispatch(requestTripsDetails('departs'));
      dispatch(requestTripsDetails('returns'));
    }

    return Promise.all(polls)
      .then((rawTrips) => {
        const trips = camelizeKeys(rawTrips, camelizeConverter).map((rawTrip) => rawTrip.payload);
        const formattedTrips = {
          oneWayPurchases: trips.map(({ trip }) => ({ trip })),
        };
        const busCategories = getAvailableBusCategories(formattedTrips);
        const totalFragmentsCount = trips.length;
        const departureFragmentsCount = tenantSupportsTripsWithConnections
          ? 1
          : departureFragments.length;
        const tripsByWay = {
          departs: trips.slice(0, departureFragmentsCount),
          returns: trips.slice(departureFragmentsCount, totalFragmentsCount),
        };

        ['departs', 'returns'].forEach((way) => {
          const wayTrips = tripsByWay[way];
          const firstTrip = wayTrips[0];
          const tripSlug = firstTrip && firstTrip.trip.id;
          const hasConnections = firstTrip ? firstTrip.hasConnections : false;
          const bus = hasConnections ? {} : firstTrip?.bus;
          if (wayTrips.length === 1 && bus) {
            const trip = firstTrip;
            if (hasConnections) {
              const layouts = getConnectionTripsBusLayouts(trip.trips);
              const seats = getConnectionTripsSeats(trip.trips);
              dispatch(
                setSeatMap(
                  way,
                  firstTrip,
                  tripSlug,
                  saveIn,
                  busCategories,
                  layouts,
                  seats,
                  trip.trips,
                ),
              );
            } else {
              dispatch(setSeatMap(way, firstTrip, tripSlug, saveIn, busCategories));
            }
          }
          if (saveInPurchase) {
            dispatch(setBusCategories(busCategories, tripSlug));
            dispatch(receiveTripsDetails(way, tripSlug));
          }
        });
      })
      .catch((reason) => {
        if (!saveInPurchase) dispatch(setSeatsBusError({ tripId: tripIds[0] }));
        dispatch(setError(203, 'error_when_consulting_a_trip', 'error', saveInPurchase));
        trackBusSchemeError({
          code: reason?.code || reason?.status,
          message: reason?.message,
          tripId: tripIds[0],
        });
        console.error(reason);
      })
      .finally(() => {
        dispatch(updatePurchase(false));
      });
  };
}

export function deletePassenger(token, passengerId) {
  return (dispatch) => {
    dispatch(updatePurchase(true));

    return purchaseApi
      .deletePassenger(token, passengerId)
      .then(({ passengers }) => {
        dispatch(setPassengers(decamelizeKeys(passengers), token));
      })
      .catch((reason) => {
        dispatch(setError(204, reason.message, 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(updatePurchase(false));
      });
  };
}
/**
 * Selects seats for a specified trip way and saves them based on the context.
 * @param {string} way - The direction of the trip (e.g., 'departure', 'return').
 * @param {Array} seats - An array of seat identifiers to be selected.
 * @param {string} saveIn - The context in which the seat selection is being saved ('purchase' or other).
 * @returns {Object} The action object with type and payload for seat selection.
 */
export function selectSeats(way, seats, saveIn = 'purchase', tripSlug) {
  return {
    type: saveIn !== 'purchase' ? types.SELECT_SEATS_TRIPS : types.SELECT_SEATS,
    way,
    seats,
    tripSlug,
  };
}

export function updateSeat(way, seat, saveIn = 'purchase', tripSlug) {
  return {
    type: saveIn !== 'purchase' ? types.UPDATE_SEAT_TRIPS : types.UPDATE_SEAT,
    way,
    seat,
    tripSlug,
  };
}

export function finishSeatSelection() {
  return (dispatch, getState) => {
    const state = getState();
    const { purchase, whitelabelConfig } = state;
    const { features } = whitelabelConfig;
    const purchaseJS = purchase.toJS();
    const { allowsSeatSelection, departs, returns, passengerSelection } = purchaseJS;

    const walletType = getWalletTypeForPurchase();
    const mainUser = getUserByWalletType(walletType);

    const passengersWithSeats = getInitialPassengers({
      allowsSeatSelection,
      departureSelectedSeats: departs.selectedSeats,
      returnSelectedSeats: returns?.selectedSeats,
      passengerSelection,
      departureTrips: departs.trips,
      returnTrips: returns?.trips || [],
    });

    dispatch({ type: types.FINISHED_SEAT_SELECTION, features, mainUser, passengersWithSeats });
  };
}

export function clearSeats(way) {
  return (dispatch) => {
    dispatch(selectSeats(way, []));
  };
}

export function requestTickets(way) {
  return { type: types.REQUEST_TICKETS, way };
}

export function receiveTickets(way, tickets, error = null) {
  return { type: types.RECEIVE_TICKETS, way, tickets, error };
}

export function refreshBus() {
  return (dispatch) => {
    const { departs, returns = {} } = store.getState().purchase.toJS();
    dispatch(
      fetchTripsDetails({
        departureFragments: departs.fragments,
        returnFragments: returns.fragments,
      }),
    );
  };
}

/**
 * For purchase with connections, each passenger can have multiple tickets for each trip (departure and return).
 * This method asignates seats to each passenger and creates a ticket for each selected seat.
 * Each ticket has the following structure:
 *
 * ```js
 *  {
 *   passenger_id: 1,
 *   category: 'general',
 *   seat: '1A',
 *   trip_slug: 'trip-slug'
 *  }
 * ```
 *
 * @returns {Array} - Returns an array of tickets for each selected seat.
 *
 */
const getTicketsForTripWithConnections = ({
  seats,
  trips,
  passengers,
  isOpenTicket,
  justAdultOpenTicket,
}) => {
  const tickets = [];
  const cleanedWaySeats = seats.filter((seat) => !seat.isPickedAsAdjacent);
  // generate tickets for each passenger, each passenger can have multiple tickets for each trip
  // so we need to iterate over the passengers and the trips
  passengers.forEach((passenger, passengerIndex) => {
    trips.forEach((trip) => {
      const tripSeats = cleanedWaySeats.filter((seat) => seat.tripSlug === trip.trip.id);
      const passengerTripSeat = tripSeats[passengerIndex];
      const category = justAdultOpenTicket && isOpenTicket ? 'general' : passenger.busCategory;
      const ticket = {
        passenger_id: passenger.id,
        category,
        trip_slug: trip.trip.id,
        seat: passengerTripSeat.number,
        ...(passengerTripSeat?.seatFareDescription && {
          seat_fare_description: passengerTripSeat.seatFareDescription,
        }),
        ...(passengerTripSeat?.seatFareId && {
          seat_fare_id: passengerTripSeat.seatFareId,
        }),
        ...(passengerTripSeat?.dynamicPrice && {
          dynamic_price: passengerTripSeat.dynamicPrice,
        }),
        ...(passengerTripSeat?.dynamicPriceDollar && {
          dynamic_price_dollar: passengerTripSeat.dynamicPriceDollar,
        }),
      };
      tickets.push(ticket);
    });
  });
  return tickets;
};

/**
 * Serializes seats data for locking
 * @param {Array} trips - Array of trips
 * @param {Array} passengers - Array of passengers
 * @param {Array} seats - Array of seats
 * @param {string} way - Trip direction ('OUTBOUND' or 'INBOUND')
 * @returns {Array} - Serialized seats data
 */
function serializeLockSeats(trips, passengers, seats = [], way) {
  const { purchase, whitelabelConfig } = store.getState();
  const { returns, departs } = purchase.toJS();
  const { JUST_ADULT_OPEN_TICKET } = whitelabelConfig.features;

  const busPassengers = passengers.filter((p) => p.category !== 'infant');
  const flightPassengers = passengers;
  const isOpenTicket = way === 'departure' ? wayIsOpenTicket(departs) : wayIsOpenTicket(returns);

  let busTickets = [];
  const wayObject = way === 'departure' ? departs : returns;
  if (wayObject.hasConnections) {
    /**
     * @todo implement adjacent seats for purchase with connections, find a way to unify the logic
     */
    busTickets = getTicketsForTripWithConnections({
      seats,
      trips,
      passengers: busPassengers,
      isOpenTicket,
      justAdultOpenTicket: JUST_ADULT_OPEN_TICKET,
    });
  } else {
    // TODO: Implementar una estrategia con la cual dependiendo de si se tiene asientos contiguos o no, se utilice map o reduce dependiendo del caso
    busTickets = busPassengers.reduce((acc, passenger, index) => {
      const seatsWithoutAdjacent = seats.filter((seat) => !seat.isPickedAsAdjacent);
      const seat = seatsWithoutAdjacent[index];
      let category = passenger.busCategory;
      if (
        (JUST_ADULT_OPEN_TICKET && isOpenTicket) ||
        (passenger.busCategory === 'oversold' && Boolean(seat.number))
      ) {
        category = 'general';
      }
      const newTicket = {
        passenger_id: passenger.id,
        category,
        ...(seat && { seat: seat.number }),
        ...(seat?.seatLevel && { seat_level: seat.seatLevel }),
        ...(seat?.seatFloor && { seat_floor: seat.seatFloor }),
        ...(seat?.seatFareId && { seat_fare_id: seat.seatFareId }),
        ...(seat?.seatFareDescription && { seat_fare_description: seat.seatFareDescription }),
        ...(seat?.dynamicPrice && { dynamic_price: seat.dynamicPrice }),
        ...(seat?.dynamicPriceDollar && { dynamic_price_dollar: seat.dynamicPriceDollar }),
        ...((seat?.rgbColor ?? seat?.rgb_color) && { rgb_color: seat.rgbColor ?? seat.rgb_color }),
      };

      if (seat && seat.isAdjacentPicked) {
        const adjacentSeatNumber = seat.adjacentSeats.numbers[0];
        newTicket.adjacent_seat = adjacentSeatNumber;
        const adjacentTicket = {
          passenger_id: passenger.id,
          seat: adjacentSeatNumber,
          category: 'general',
        };
        acc.push(newTicket, adjacentTicket);
      } else {
        acc.push(newTicket);
      }
      return acc;
    }, []);
  }

  const flightTickets = flightPassengers.map((passenger) => ({
    passenger_id: passenger.id,
    category: passenger.category,
  }));

  const [trip] = trips;
  if (trip.type === 'bus' || wayObject.hasConnections) {
    return {
      trip_slug: wayObject.hasConnections ? wayObject.tripSlug : trip.id,
      tickets: busTickets,
    };
  }
  return {
    trip_slug: trip.id,
    tickets: flightTickets,
  };
}

export function lockTicketsLegacy(way, token, trips, passengers, seats = []) {
  const busPassengers = passengers.filter((p) => p.category !== 'infant');
  const flightPassengers = passengers;

  const busTickets = busPassengers.map((passenger, index) => ({
    passenger_id: passenger.id,
    category: passenger.busCategory,
    ...(seats[index] && { seat: seats[index] }), // Only include seat if it exists
  }));

  const flightTickets = flightPassengers.map((passenger) => ({
    passenger_id: passenger.id,
    category: passenger.category,
  }));

  const payloads = trips.map((trip) => {
    if (trip.type === 'bus') {
      return { trip_slug: trip.id, tickets: busTickets };
    }

    return { trip_slug: trip.id, tickets: flightTickets };
  });

  return (dispatch) => {
    dispatch(requestTickets(way));

    const polls = pollingPromises({
      data: payloads,
      name: 'onReceiveTickets',
      create: (polling, payload) => purchaseApi.createTickets(token, payload, polling),
    });

    return Promise.all(polls)
      .then((polls) => {
        polls.forEach(({ status }) => {
          checkIfRetriesExceeded(status);
        });

        const tickets = polls.map(({ payload }) => payload.tickets);

        dispatch(receiveTickets(way, tickets));
        return dispatch(getPurchase(token, true));
      })
      .catch((reason) => {
        const knownErrors = ['occupied'];
        if (knownErrors.includes(reason.type)) {
          dispatch(setError(204, reason.message, 'warning', false));
          dispatch(receiveTickets(way, []));
        } else {
          dispatch(setError(204, 'Ocurrió un error al reservar tus asientos'));
        }
        trackLockSeatsError({ code: reason?.type || reason?.code, message: reason?.message });
        console.error(reason);
      });
  };
}

/**
 * Function that handles the error when polling tickets
 * @param {Object} reason - Error object
 * @returns dispatch
 */
// Error code returned by platform-purchase when seat/passenger edits are blocked because a
// store payment reference already exists for the purchase (AM-1686).
const STORE_REFERENCE_EDIT_BLOCKED_CODE = 60;

function lockTicketsPollingError(reason) {
  return (dispatch) => {
    trackLockSeatsError({ code: reason?.payload?.errors?.state?.[0], message: reason?.message });
    const { purchase } = store.getState();
    const { isExchange } = purchase.toJS();
    const { payload } = reason;

    // A blocked edit is a 422 on the initial ticket_requests POST, so it arrives as
    // { errors: [{ code, message }] } (no `payload`). Surface the backend message instead of
    // the generic "trip no longer available" / "try to change your seats".
    const blockedError = Array.isArray(reason?.errors)
      ? reason.errors.find((error) => error?.code === STORE_REFERENCE_EDIT_BLOCKED_CODE)
      : null;
    if (blockedError) {
      dispatch(setError(204, 'try_to_change_your_seats', 'error', false, blockedError.message));
      dispatch(receiveTickets('departs', [], blockedError.message));
      dispatch(receiveTickets('returns', [], blockedError.message));
      dispatch(refreshBus());
      return;
    }

    if (!payload) {
      dispatch(setError(204, 'trip_no_longer_available', 'warning', true));
      return;
    }

    const { errors } = payload;
    const { state } = errors;
    const [error] = state;

    if (/occupied/g.test(error)) {
      const message = error.split(':')[1] || 'try_to_change_your_seats';
      dispatch(setError(204, message, 'error', false));
    } else if (/unavailable_wallet/g.test(error)) {
      const message = error.split(':')[1] || 'try_to_change_your_seats';
      dispatch(setError(204, message, 'error', true, null, isExchange && EXCHANGE_HOME_PATH));
    } else if (/unavailable/g.test(error)) {
      dispatch(setError(204, 'discounts_not_available', 'warning', false));
    } else if (/exchange_limit_reached/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(
        setError(
          204,
          'try_to_change_your_seats',
          'error',
          true,
          message,
          isExchange && EXCHANGE_HOME_PATH,
        ),
      );
    } else if (/invalid_exchange/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(setError(204, 'exchange_cannot_be_used', 'warning', true, message));
    } else if (
      /exchange_time_exceed|exchange_after_departure|exchange_enable_time_exceeded/g.test(error)
    ) {
      const message = error.split(':')[1];
      const exchangeRedirectPath = isExchange ? getExchangeLockScheduleRedirectPath() : null;
      dispatch(
        setError(
          204,
          'exchange_cannot_be_used',
          'error',
          Boolean(exchangeRedirectPath),
          message,
          exchangeRedirectPath || undefined,
        ),
      );
    } else if (/exchange_in_progress/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(setError(204, 'exchange_in_progress', 'warning', true, message));
    } else if (/older_category_limit_reached/g.test(error)) {
      const message = error.split(':')[1];
      dispatch(setError(204, message, 'error', false));
    } else if (/lap_child_gratuity_lock_failed/g.test(error)) {
      const message = error.split(':').slice(1).join(':') || null;
      dispatch(setError(204, 'try_to_change_your_seats', 'error', false, message));
    } else {
      dispatch(setError(204, 'try_to_change_your_seats', 'error', false));
    }
    dispatch(receiveTickets('departs', [], error));
    dispatch(receiveTickets('returns', [], error));
    if (!/unavailable/g.test(error)) dispatch(clearSeats('departs'));
    if (!/unavailable/g.test(error)) dispatch(clearSeats('returns'));
    dispatch(refreshBus());
    console.error(reason);
  };
}

export function lockTickets(
  token,
  { departs, returns },
  passengers,
  { preservePaymentOptionSelection = false } = {},
) {
  return (dispatch, getState) => {
    const purchase = getState().purchase.toJS();

    const payloads = {};
    const departureSeats = departs[0].selectedSeats;

    if (purchase.departs.hasConnections) {
      payloads.departs = serializeLockSeats(
        purchase.departs.trips || [],
        passengers,
        departureSeats,
        'departure',
      );
    } else {
      payloads.departs = serializeLockSeats(departs, passengers, departureSeats, 'departure');
    }

    if (returns) {
      const returnSeats = returns[0].selectedSeats;
      if (purchase.returns?.hasConnections) {
        payloads.returns = serializeLockSeats(
          purchase.returns.trips || [],
          passengers,
          returnSeats,
          'returns',
        );
      } else {
        payloads.returns = serializeLockSeats(returns, passengers, returnSeats, 'returns');
      }
    }

    dispatch(requestTickets('departs'));
    if (returns) dispatch(requestTickets('returns'));

    const polls = pollingPromises({
      data: [payloads],
      name: 'onReceiveTickets',
      create: (polling, payload) => purchaseApi.createTickets(token, payload, polling),
    });

    return Promise.all(polls)
      .then((polls) => {
        polls.forEach(({ status }) => {
          checkIfRetriesExceeded(status);
        });

        const departsTickets = polls.map(({ payload }) => payload.departs.tickets);
        dispatch(receiveTickets('departs', departsTickets));

        if (returns) {
          const returnsTickets = polls.map(({ payload }) => payload.returns.tickets);
          dispatch(receiveTickets('returns', returnsTickets));
        }
        return dispatch(getPurchase(token, true, { preservePaymentOptionSelection }));
      })
      .catch((reason) => {
        dispatch(lockTicketsPollingError(reason));
      });
  };
}

export function clearTicketsErrorType() {
  return {
    type: types.CLEAR_TICKETS_ERROR_TYPE,
  };
}

/**
 * Function that removes the file size segment from the document
 *
 * @param {string} document - The document with the file size segment
 * @returns {string} The document without the file size segment
 */
export function documentWithoutSize(document) {
  const originalDataSegment = document.split('::')[1];
  return originalDataSegment;
}

export function savePassengersWithTickets(token, fields, trips) {
  return (dispatch, getState) => {
    const state = getState();
    const { env, features } = selectWhitelabelConfig(state);
    const purchase = selectPurchase(state);
    const normalizedFields = { ...fields };
    const phoneCodeFallback = fields.phoneCode ?? null;
    normalizedFields.phoneCode = phoneCodeFallback;
    const normalizedPhoneCountry = fields.phoneCountry?.trim()?.toUpperCase();

    if (normalizedPhoneCountry) {
      normalizedFields.phoneCountry = normalizedPhoneCountry;
      try {
        normalizedFields.phoneCode = getCountryCallingCode(normalizedPhoneCountry);
      } catch (error) {
        normalizedFields.phoneCode = phoneCodeFallback;
      }
    }

    const purchasePayloadTypes = {
      gfa: decamelizeKeys({
        purchaserNationalityId: 1,
        ...omit(normalizedFields, 'passengers'),
        email: normalizedFields.email,
      }),
      default: decamelizeKeys({
        purchaserNationalityId: 1,
        omitEmailValidation: true, // This is used to omit the email validation for the purchaser so doesn't affect the passenger email validation
        ...omit(normalizedFields, 'passengers'),
        email: features.NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD
          ? normalizedFields.passengers[0].email
          : normalizedFields.email,
      }),
    };
    const { availableWallets = [] } = purchase;
    const purchasePayloadBase = purchasePayloadTypes[env.brand] ?? purchasePayloadTypes.default;

    // Get selected products from cart state to include ancillaries with passengers
    const selectedProducts = selectSelectedProducts(state);
    const ancillariesByPassenger = transformProductsToAncillaries(selectedProducts);

    const brand2ProcessorMap = getBrand2ProcessorMap();
    const passengersPayload = decamelizeKeys(
      fields.passengers.map((passenger, index) => {
        const passengerAncillaries = ancillariesByPassenger[index] || {
          outgoing_ancillaries: [],
          incoming_ancillaries: [],
        };

        return {
          ...formatName(stripUiOnlyPassengerMeta(passenger)),
          ...(features.DOCUMENT_PER_PASSENGER && {
            document: documentWithoutSize(passenger?.document),
          }),
          nationalityId: purchasePayloadBase.purchaser_nationality_id,
          document_type: brand2ProcessorMap[passenger.documentType],
          isoCountryCode: passenger.nationality,
          phone: normalizeRegex('[^0-9]', passenger.phone),
          ...passengerAncillaries,
        };
      }),
    );

    dispatch(postPassengers());

    // The user token is set in the requests
    setUserTokenForAPI();
    // Getting the wallet type to update the purchase
    const walletType = getWalletTypeForPurchase();
    const isValidWalletType = walletType && availableWallets?.includes(walletType);

    const purchasePayload = {
      ...purchasePayloadBase,
      wallet_type: isValidWalletType ? walletType : null,
      used_frequent_passengers: fields.passengers.some(
        (passenger) => passenger.isFrequentPassenger,
      ),
      used_autoselected_passenger: fields.passengers.some(
        (passenger) => passenger.preferredPassengerOrder,
      ),
    };

    return purchaseApi
      .update(token, purchasePayload)
      .then((purchaseResponse) => {
        if (purchaseResponse.purchase.passengers.length) {
          return purchaseApi.updatePassengers(token, passengersPayload);
        }

        passengersPayload.forEach((passenger) => {
          Reflect.deleteProperty(passenger, 'id');
        });
        return purchaseApi.createPassengers(token, passengersPayload);
      })
      .then((payload) => {
        const { departs, returns } = trips;
        const { passengers } = camelizeKeys(payload);
        const { features } = getState().whitelabelConfig;
        const departureSeats =
          departs[0].selectedSeats && departs[0].selectedSeats.map(({ number }) => number);

        if (features.USE_LEGACY_LOCK_TICKETS) {
          dispatch(lockTicketsLegacy('departs', token, departs, passengers, departureSeats));

          if (returns) {
            const returnSeats =
              returns[0].selectedSeats && returns[0].selectedSeats.map(({ number }) => number);
            dispatch(lockTicketsLegacy('returns', token, returns, passengers, returnSeats));
          }
        } else {
          dispatch(lockTickets(token, { departs, returns }, passengers));
        }
        dispatch(setPassengers(passengers, token));
      })
      .catch((reason) => {
        dispatch(setPassengers([], token));

        // Check if this is a product unavailability error
        if (isProductUnavailableError(reason)) {
          const productCodes = extractUnavailableProductCodes(reason);
          if (productCodes.length > 0) {
            dispatch(handleProductUnavailable({ productCodes }));
            // Show user-friendly error message
            dispatch(setError(202, 'product_unavailable_error', 'warning', false));
          }
          return;
        }

        // Check if this is a passenger email validation error
        if (isPassengerEmailValidationError(reason)) {
          handleEmailValidationError({
            reason,
            dispatch,
            formName: 'passengers',
            errorCode: 202,
            errorMessage: 'passenger_email_validation_error',
          });
          return;
        }

        // Fallback to general error handling — surface backend message verbatim if present
        const errorMessage = reason?.details?.errors?.[0]?.message;
        dispatch(setError(202, reason.message, 'error', false, errorMessage));
        console.error(reason);
      });
  };
}

/**
 * Saves multiple passengers for bulk ticket purchases
 * @param {string} token - Purchase token
 * @param {Array<Object>} passengerPayload - Array of passenger objects containing passenger details
 * @returns {Function} Redux thunk action
 */
export function saveBulkPassengers(token, passengerPayload) {
  return (dispatch, getState) => {
    // Follows the same logic as savePassengersWithTickets but for bulk tickets
    // Considerate: bulk tickets have no seats and no returns
    dispatch(updatePurchase(true));
    const { env, features } = getState().whitelabelConfig;
    const purchasePayloadTypes = {
      gfa: decamelizeKeys({
        purchaserNationalityId: 1,
        email: passengerPayload[0].email,
      }),
      conectagfa: decamelizeKeys({
        purchaserNationalityId: 1,
        email: passengerPayload[0].email,
      }),
      default: decamelizeKeys({
        purchaserNationalityId: 1,
        email: features.NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD ? passengerPayload[0].email : '',
      }),
    };
    const purchasePayload = purchasePayloadTypes[env.brand] ?? purchasePayloadTypes.default;

    const brand2ProcessorMap = getBrand2ProcessorMap();
    const passengersPayload = decamelizeKeys(
      passengerPayload.map((passenger) => ({
        ...formatName(stripUiOnlyPassengerMeta(passenger)),
        ...(features.DOCUMENT_PER_PASSENGER && {
          document: documentWithoutSize(passenger?.document),
        }),
        nationalityId: purchasePayload.purchaser_nationality_id,
        document_type: brand2ProcessorMap[passenger.documentType],
        isoCountryCode: passenger.nationality,
        phone: normalizeRegex('[^0-9]', passenger.phone),
      })),
    );

    setUserTokenForAPI();

    return purchaseApi
      .update(token, purchasePayload)
      .then((purchaseResponse) => {
        if (purchaseResponse.purchase.passengers.length) {
          return purchaseApi.updatePassengers(token, passengersPayload);
        }

        passengersPayload.forEach((passenger) => {
          Reflect.deleteProperty(passenger, 'id');
        });
        return purchaseApi.createPassengers(token, passengersPayload);
      })
      .then((payload) => {
        const { passengers } = camelizeKeys(payload);
        const { departs, returns, roundTrip } = store.getState().purchase.toJS();

        // create payloads for tickets without seats
        const payloads = {
          departs: {
            trip_slug: departs.tripSlug || departs.fragments[0].id,
            tickets: passengers.map((passenger) => ({
              passenger_id: passenger.id,
              category: passenger.busCategory || 'general',
            })),
          },
          returns: roundTrip
            ? {
                trip_slug: returns.tripSlug || returns.fragments[0].id,
                tickets: passengers.map((passenger) => ({
                  passenger_id: passenger.id,
                  category: passenger.busCategory || 'general',
                })),
              }
            : undefined,
        };

        dispatch(requestTickets('departs'));

        const polls = pollingPromises({
          data: [payloads],
          name: 'onReceiveTickets',
          create: (polling, payload) => purchaseApi.createTickets(token, payload, polling),
        });

        return Promise.all(polls)
          .then((polls) => {
            polls.forEach(({ status }) => {
              checkIfRetriesExceeded(status);
            });

            const departsTickets = polls.map(({ payload }) => payload.departs.tickets);
            dispatch(receiveTickets('departs', departsTickets));
            dispatch(getPurchase(token, true));
            dispatch(setPassengers(passengers, token));
            // Disable isUpdating after successful completion
            dispatch({ type: types.UPDATE_PURCHASE, updating: false });
          })
          .catch((reason) => {
            dispatch(setError(202, reason.message, 'error', false));
            // Disable isUpdating in case of error
            dispatch({ type: types.UPDATE_PURCHASE, updating: false });
            console.error(reason);
          });
      })
      .catch((reason) => {
        dispatch(setError(202, reason.message, 'error', false));
        // Disable isUpdating in case of error
        dispatch({ type: types.UPDATE_PURCHASE, updating: false });
        console.error(reason);
      });
  };
}

export function fetchingTaxpayerId(updating) {
  return {
    type: types.FETCHING_TAXPAYER_ID,
    updating,
  };
}

/**
 * Validates the Taxpayer ID for Cruz del Sur
 *
 * @param {string} taxpayerId - The taxpayer ID to validate
 * @returns {Function} A thunk action creator
 */
export function validateTaxpayerId(taxpayerId) {
  return (dispatch, getState) => {
    if (taxpayerId.length < 11) {
      dispatch(changeFormField('purchaser', 'taxpayerLegalName', ''));
      dispatch(changeFormField('purchaser', 'taxpayerAddress', ''));
      return;
    }
    const state = getState();
    const purchaseToken = state.purchase.toJS().token;
    const params = { taxpayerId, purchaseToken };
    return purchaseApi
      .validateTaxPayer(params)
      .then((taxPayerResponse) => {
        const camelizedTaxPayerResponse = camelizeKeys(taxPayerResponse);
        const { taxpayerLegalName, taxpayerAddress } = camelizedTaxPayerResponse.body;

        if (!taxpayerLegalName || taxpayerLegalName === 'unknown or empty RUC parameter') {
          dispatch(changeFormField('purchaser', 'taxpayerLegalName', 'RUC Invalido'));
          dispatch(changeFormField('purchaser', 'taxpayerAddress', ''));
          throw new Error('Invalid RUC');
        }

        dispatch(changeFormField('purchaser', 'taxpayerLegalName', taxpayerLegalName));
        dispatch(changeFormField('purchaser', 'taxpayerAddress', taxpayerAddress || ''));
      })
      .catch((error) => {
        dispatch(changeFormField('purchaser', 'taxpayerLegalName', 'RUC Invalido'));
        throw new Error('Invalid RUC', error);
      })
      .finally(() => {
        dispatch(fetchingTaxpayerId(false));
      });
  };
}

/**
 * Function to validate the change of document
 * @param {Object} params - The parameters for the change document validation
 * @param {string} params.taxpayerId - The taxpayer ID to validate
 * @param {string} params.ticket - The ticket to validate
 * @returns {Function} A thunk action creator that returns the validation response
 */
export function validateChangeDocument(params) {
  return (dispatch) => {
    dispatch({ type: types.VALIDATE_CHANGE_DOCUMENT_LOADING, isLoading: true });
    return purchaseApi
      .validateChangeDocument(params)
      .then((response) => {
        dispatch({ type: types.VALIDATE_CHANGE_DOCUMENT_RECEIVE, data: response });
        return response;
      })
      .catch((error) => {
        const errorResponse = { success: false, message: error.message || 'Validation failed' };
        return errorResponse;
      })
      .finally(() => {
        dispatch({ type: types.VALIDATE_CHANGE_DOCUMENT_LOADING, isLoading: false });
      });
  };
}

/**
 * Function to change the document
 * @param {Object} params - The parameters for the document change
 * @returns {Function} A thunk action creator that returns the change document response
 */
export function changeDocument(params) {
  return (dispatch) => {
    dispatch({ type: types.CHANGE_DOCUMENT_LOADING, isLoading: true });

    return purchaseApi
      .changeDocument(params)
      .then((response) => {
        dispatch({ type: types.CHANGE_DOCUMENT_RECEIVE, data: response });
        return response;
      })
      .catch((error) => {
        const errorResponse = {
          success: false,
          message: error.message || 'Change document failed',
        };
        return errorResponse;
      })
      .finally(() => {
        dispatch({ type: types.CHANGE_DOCUMENT_LOADING, isLoading: false });
      });
  };
}

/**
 * Fetches and populates client data based on the provided document type and ID.
 *
 * @param {string} formName - The name of the form to update with client data.
 * @param {string} documentType - The type of the document (e.g., passport, ID).
 * @param {string} documentNumber - The document number used for fetching client data.
 * @returns {Function} A thunk action creator that dispatches form field updates.
 */
export function fetchClientDataByDocument(formName, documentType, documentNumber) {
  return (dispatch, getState) => {
    if (!documentNumber) return;

    const state = getState();
    const purchaseToken = state.purchase.get('token');

    const params = {
      documentType,
      documentNumber,
      purchaseToken,
    };

    return purchaseApi
      .autocompleteByDNIDocument(params)
      .then((documentResponse) => {
        const camelizedDocumentResponse = camelizeKeys(documentResponse);

        const { birthDate, name, lastName, secondLastName, nationality } =
          camelizedDocumentResponse;
        const [day, month, year] = birthDate.split('/');
        const formattedBirthDate = `${year}-${month}-${day}`;
        dispatch(changeFormField('passengers', `${formName}.firstName`, name));
        dispatch(changeFormField('passengers', `${formName}.lastName`, lastName));
        dispatch(changeFormField('passengers', `${formName}.secondLastName`, secondLastName));
        dispatch(changeFormField('passengers', `${formName}.dateOfBirth`, formattedBirthDate));
        dispatch(changeFormField('passengers', `${formName}.nationality`, nationality));
      })
      .catch((error) => console.error('Error fetching client data: ', error));
  };
}

export function toggleInsurance(updating) {
  return { type: types.TOGGLE_INSURANCE, updating };
}

export function toggleWantsInsurance(token, wants) {
  return (dispatch) => {
    dispatch(toggleInsurance(true));

    return purchaseApi
      .update(token, { wants_insurance: wants })
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleInsurance(false));
      });
  };
}

export function toggleUsingWallet(updating) {
  return { type: types.UPDATE_USING_WALLET, updating };
}

export function requestDiscount() {
  return { type: types.REQUEST_DISCOUNT };
}

export function receiveDiscount() {
  return { type: types.RECEIVE_DISCOUNT };
}

export function setUsingWallet(token, useWallet, amount = null, updating) {
  return (dispatch) => {
    const { walletType } = store.getState().purchase.toJS();
    if (amount && amount <= 0) {
      dispatch(setError(205, 'error_when_using_points', 'error', false));
      return;
    }

    dispatch(toggleUsingWallet(true));

    // return a promise to wait for
    return purchaseApi
      .updateWallet(token, updating ? false : useWallet, { amount })
      .then((payload) => {
        if (updating) return dispatch(setUsingWallet(token, true, amount));

        const message = useWallet
          ? `buy_using_your_points_${walletType}`
          : `buy_without_using_your_points_${walletType}`;
        const messageType = useWallet ? 'success' : 'warning';

        dispatch(resetInstallmentsPlan());
        dispatch(changeFormField('card', 'paymentPlan', 'single'));
        dispatch(resetForm('installmentsSelector'));
        dispatch(receivePurchase(payload));
        dispatch(setError(null, message, messageType, false));
      })
      .catch((reason) => {
        if (reason.code === 'INVALID_USER') {
          dispatch(setError(205, 'user_is_not_a_passenger', 'warning', false));
        } else {
          dispatch(setError(205, 'error_when_using_points', 'error', false));
        }
        console.error(reason);
      })
      .finally(() => dispatch(toggleUsingWallet(false)));
  };
}

export function applyDiscount(token, code, selectedInstallmentsPlan) {
  return (dispatch) => {
    dispatch(requestDiscount());

    return purchaseApi
      .applyDiscountCode(token, code, selectedInstallmentsPlan)
      .then((payload) => {
        const { qualifiesForMonthlyInstallments, paymentPlans } = camelizeKeys(payload.purchase);
        const i18nKey = 'code_applied_to_purchase';

        // When a discount is applied, the backend returns updated paymentPlans with new amounts.
        // Check if the user's currently selected installment plan is still available.
        // If available, update it with the new post-discount amounts to keep UI in sync.
        // If not available, reset the installments selection.
        const currentPlanAvailable = isPlanAvailable(
          qualifiesForMonthlyInstallments,
          selectedInstallmentsPlan,
          paymentPlans,
        );

        if (currentPlanAvailable) {
          const { card, months } = selectedInstallmentsPlan;
          const planData = paymentPlans?.[card]?.[months];
          if (planData) {
            const total =
              planData.total || (planData.monthlyPayment ? months * planData.monthlyPayment : 0);
            payload.purchase.monthly_selected_plan = months;
            payload.purchase.selectedInstallmentsPlan = {
              ...selectedInstallmentsPlan,
              ...planData,
              total,
            };
          }
        } else {
          dispatch(resetInstallmentsPlan());
          dispatch(changeFormField('card', 'paymentPlan', 'single'));
          dispatch(resetForm('installmentsSelector'));
        }

        dispatch(setPaymentPlans(paymentPlans));
        dispatch(receivePurchase(payload));
        dispatch(setError(null, i18nKey, 'success', false));
      })
      .catch((reason) => {
        dispatch(resetForm('discountCode'));
        dispatch(
          setError(
            206,
            reason.code === 500 ? 'invalid_discount' : 'error_when_applying_discount_code',
            'error',
            false,
          ),
        );
      })
      .finally(() => {
        dispatch(receiveDiscount());
      });
  };
}

export function formErrors(funnelStep, fields) {
  return {
    type: types.UA_FORM_ERRORS,
    funnelStep,
    fields,
  };
}

export function expirePurchase() {
  return {
    type: types.EXPIRE_PURCHASE,
  };
}

export function setPurchaseExpiration(expiration) {
  return {
    type: types.PURCHASE_SET_EXPIRATION,
    expiration,
  };
}

export function timeoutTick(milisecondsAdded) {
  return (dispatch, getState) => {
    const { purchase } = getState();

    const remainingTime = getExpirationTime(purchase.toJS(), milisecondsAdded);

    if (remainingTime <= 0) {
      dispatch(expirePurchase());
    }
  };
}

export function setEmailStatus(status) {
  return {
    type: types.SET_EMAIL_STATUS,
    status,
  };
}

export function sendEmail(token, email) {
  return (dispatch) => {
    dispatch(setEmailStatus('sending'));
    purchaseApi
      .sendEmail(token, email)
      .then(() => dispatch(setEmailStatus('sent')))
      .catch(() => dispatch(setEmailStatus('error')));
  };
}

/**
 * Update purchase total for bulk tickets
 * @param {*} passengers - Passengers
 * @returns - Dispatch action
 */
export function updateBulkPurchaseTotal(passengers) {
  const { purchase } = store.getState();
  const { departs, returns, roundTrip } = purchase.toJS();

  const departsPrices = departs.fragments[0].prices;
  const { total: departsTotal } = purchaseTotalByCategory(passengers, departsPrices);

  let total = departsTotal;

  // Add return total only if it's a round trip
  if (roundTrip) {
    const returnsPrices = returns.fragments[0].prices;
    const { total: returnsTotal } = purchaseTotalByCategory(passengers, returnsPrices);
    total += returnsTotal;
  }

  // Dispatch the action with the new total
  return {
    type: types.UPDATE_PURCHASE_TOTAL,
    total,
  };
}

function receivePurchaser(purchaser) {
  return {
    type: types.RECEIVE_PURCHASER,
    purchaser: camelizeKeys(purchaser),
  };
}

export function updatePaymentPlans(paymentPlans) {
  return {
    type: types.UPDATE_PAYMENT_PLANS,
    paymentPlans,
  };
}

/**
 * Action creator to toggle service levels loading state
 * @param {boolean} updating - Whether service levels are being updated
 * @returns {object} Action object
 */
function toggleServiceLevels(updating) {
  return { type: types.UPDATE_SERVICE_LEVEL, updating };
}

/**
 * Thunk action creator to toggle service levels for a purchase
 * @param {string} token - Purchase token
 * @param {string} serviceLevelId - Service level ID
 * @returns {Function} Thunk function
 */
export function toggleWantsServiceLevels(token, serviceLevelId) {
  const payload = { service_level_id: serviceLevelId };

  return (dispatch) => {
    dispatch(toggleServiceLevels(true));

    return purchaseApi
      .update(token, payload)
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleServiceLevels(false));
      });
  };
}

export function updatePurchaseFields(purchaseToken, fields) {
  return (dispatch) => {
    const {
      purchase,
      whitelabelConfig: {
        features,
        features: {
          NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD,
          IDENTIFICATION_DOCUMENT_FOR_PURCHASER,
          SHOW_PURCHASER_ADDRESS_FIELDS,
          SHOW_PROMOTIONAL_TERMS_CHECKBOX,
        },
        env,
      },
      cdsFrequentPassenger,
    } = store.getState();
    const { passengers } = purchase.toJS();

    const showCdsFrequentPassengerOptIn =
      isCdsFrequentPassengerEnabled(features, env.brand) &&
      isCdsFrequentPassengerBootstrapConfirmedAnonymous({
        isBootstrapped: cdsFrequentPassenger.get('isBootstrapped'),
        isAuthenticated: cdsFrequentPassenger.get('isAuthenticated'),
        error: cdsFrequentPassenger.get('error'),
      });

    const normalizedPhoneCountry = fields.phoneCountry?.trim()?.toUpperCase();
    let phoneCode;
    try {
      phoneCode = normalizedPhoneCountry
        ? getCountryCallingCode(normalizedPhoneCountry)
        : undefined;
    } catch (error) {
      phoneCode = undefined;
    }
    const phone =
      phoneCode && fields.phone && parsePhoneNumber(`+${phoneCode}${fields.phone}`)?.nationalNumber;
    const sendAddressAttributes = fields.documentType === 'NIT' && SHOW_PURCHASER_ADDRESS_FIELDS;

    const { promotionalTerms, cdsFrequentPassengerOptIn, ...fieldsWithoutCheckboxes } = fields;
    const payload = decamelizeKeys({
      ...fieldsWithoutCheckboxes,
      ...(normalizedPhoneCountry && { phoneCountry: normalizedPhoneCountry }),
      purchaserNationalityId: 1,
      documentType: IDENTIFICATION_DOCUMENT_FOR_PURCHASER ? fields.documentType : 'CC',
      ...(SHOW_PROMOTIONAL_TERMS_CHECKBOX && {
        acceptedPromotionalTerms: fields.promotionalTerms ? 'true' : 'false',
      }),
      phone,
      email: NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD ? passengers[0].email : fields.email,
      ...(normalizedPhoneCountry && { phone_code: phoneCode }),
      ...(fields.taxpayerId && {
        taxpayer_id: fields.taxpayerId,
        taxpayer_legal_name: fields.taxpayerLegalName,
        taxpayer_address: fields.taxpayerAddress || '',
      }),
      addressAttributes: sendAddressAttributes
        ? {
            ...fields.addressAttributes,
            phone_code: phoneCode,
            phone_country: fields.phoneCountry,
            phone,
          }
        : {},
      // Not run through decamelizeKeys' automatic name mapping: the backend meta key
      // (frequent_passenger_enrollment) intentionally differs from what cdsFrequentPassengerOptIn
      // would decamelize to (cds_frequent_passenger_opt_in).
      ...(showCdsFrequentPassengerOptIn && {
        frequent_passenger_enrollment: fields.cdsFrequentPassengerOptIn ? 'true' : 'false',
      }),
    });
    dispatch(updatePurchase(true));

    return purchaseApi
      .update(purchaseToken, payload)
      .then((response) => {
        /**
         * We retrieve only the fields that were updated in the payload
         * and update the purchase with them
         */
        const fieldsFromPayload = Object.keys(payload);
        const purchaser = response.purchase;
        const updatedFields = fieldsFromPayload.reduce((acc, field) => {
          acc[field] = purchaser[field];
          return acc;
        }, {});

        if (
          payload.phone_country !== undefined &&
          (updatedFields.phone_country === undefined || updatedFields.phone_country === null)
        ) {
          updatedFields.phone_country = payload.phone_country;
        }

        // accepted_promotional_terms / frequent_passenger_enrollment are stored in meta by the
        // backend; assign form values so Redux has them and purchaserUpdated becomes true.
        // Use fresh purchase from store so we merge against latest meta instead of overwriting newer state
        if (SHOW_PROMOTIONAL_TERMS_CHECKBOX || showCdsFrequentPassengerOptIn) {
          const latestPurchase = store.getState().purchase;
          const currentMeta = latestPurchase.get('meta');
          const currentMetaJs =
            currentMeta && typeof currentMeta.toJS === 'function'
              ? currentMeta.toJS()
              : currentMeta || {};
          updatedFields.meta = {
            ...currentMetaJs,
            ...(SHOW_PROMOTIONAL_TERMS_CHECKBOX && {
              accepted_promotional_terms: fields.promotionalTerms ? 'true' : 'false',
            }),
            ...(showCdsFrequentPassengerOptIn && {
              frequent_passenger_enrollment: fields.cdsFrequentPassengerOptIn ? 'true' : 'false',
            }),
          };
        }

        dispatch(receivePurchaser(updatedFields));
      })
      .catch((reason) => {
        dispatch(updatePurchase(false));

        // Check if this is a purchaser email validation error
        if (isPurchaserEmailValidationError(reason)) {
          handleEmailValidationError({
            reason,
            dispatch,
            formName: 'purchaser',
            errorCode: 204,
            errorMessage: 'purchaser_email_validation_error',
          });
          return Promise.reject(reason);
        }

        // Fallback to general error handling
        dispatch(setError(204, reason.message, 'error', false));
        return Promise.reject(reason);
      });
  };
}

export function insurancesChecked() {
  return {
    type: types.PURCHASE_INSURANCES_CHECKED,
  };
}

export function updatePurchaseUser(purchaseToken) {
  return (dispatch) => {
    dispatch(updatePurchase(true));

    return purchaseApi
      .update(purchaseToken, {})
      .then((response) => {
        dispatch(receivePurchase(response));
      })
      .catch((reason) => {
        dispatch(updatePurchase(false));
        dispatch(setError(204, reason.message, 'error', false));
        return Promise.reject(reason);
      });
  };
}

/**
 * Updates the purchase with the accountId from the user account
 * @param {Object} params - The parameters for the update
 * @param {string|number} params.accountId - The user ID to set as accountId
 * @returns {Function} Thunk action
 */
export function updatePurchaseWithReserhubAccount({ accountId }) {
  return async (dispatch, getState) => {
    const { purchase } = getState();
    const plainState = purchase.toJS();
    const { token: purchaseToken, state, accountId: purchaseAccountId } = plainState;

    if (purchaseToken && purchaseAccountId !== accountId && state !== 'completed') {
      try {
        dispatch(updatePurchase(true));
        // Dummy update to change account id with auth token in requests
        const purchaseResponse = await purchaseApi.update(purchaseToken, {});

        dispatch(receivePurchase(purchaseResponse));
        return purchaseResponse;
      } catch (error) {
        dispatch(updatePurchase(false));
        return null;
      }
    }
  };
}

/**
 * Polls the purchase status and resolves or rejects based on the purchase state.
 *
 * @param {string} purchaseToken - The token representing the purchase.
 * @param {number} [minutes=1] - The duration in minutes to keep polling.
 * @param {number} [interval=2] - The interval in seconds between each poll.
 * @returns {Promise<Object>} - A promise that resolves with the purchase payload if the purchase is completed or canceled, or rejects with the poll payload if the purchase is not pending, completed, or canceled.
 */
export function purchaseStatusResult(purchaseToken, minutes = 1, interval = 2) {
  return new Promise((resolve, reject) => {
    const pollOptions = {
      interval: interval * 1000,
      maxRetries: (minutes * 60) / interval,
    };

    const onReceivePurchase = (poll) => {
      if (!['pending', 'completed', 'canceled'].includes(poll.status)) {
        return reject(poll);
      }

      const { state } = poll.payload.purchase;
      switch (state) {
        case 'completed':
          resolve(poll.payload);
          break;
        case 'canceled':
          resolve(poll.payload);
          break;
        case 'pending':
          break;
        case 'attempt':
          break;
        default:
          reject(poll.payload);
          break;
      }
    };

    purchaseApi.get(purchaseToken, null, {
      onReceivePurchase,
      options: pollOptions,
    });
  });
}

/**
 * Polls the purchase status until the purchase is complete.
 *
 * @param {string} purchasetoken - The token representing the purchase.
 * @param {boolean} isPixPayment - Flag indicating if the payment method is Pix.
 * @returns {Function} A thunk that dispatches the purchase status result.
 */
export function pollPurchaseComplete(purchasetoken, isPixPayment) {
  const defaultMinutes = 1;
  const pixMinutes = 27;
  const pollMinutes = isPixPayment ? pixMinutes : defaultMinutes;
  return (dispatch) => {
    purchaseStatusResult(purchasetoken, pollMinutes)
      .then((purchaseResponse) => {
        dispatch(receivePurchase(purchaseResponse));
      })
      .catch((e) => {
        console.log(e);
      });
  };
}

/**
 * Carbon ancillary actions
 */
export function toggleCarbonOffset(updating) {
  return { type: types.TOGGLE_CARBON_OFFSET, updating };
}

export function toggleWantsCarbonOffset(token, wants) {
  return (dispatch) => {
    dispatch(toggleCarbonOffset(true));

    return purchaseApi
      .update(token, { wants_carbon_offset: wants })
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleCarbonOffset(false));
      });
  };
}

/**
 * Set purchase expiration after extending
 * @param {*} extendedAt - Extended date
 * @param {*} expiresAt - New expiration date
 * @param {*} currentTime - Current time of the server
 * @returns dispatch
 */
export function setPurchaseExtendedExpiration(extendedAt, expiresAt, currentTime) {
  return {
    type: types.PURCHASE_EXTENDED_EXPIRATION,
    expiresAt,
    extendedAt,
    currentTime,
  };
}

/**
 * Request to extend purchase expiration
 * @param {String} token - Purchase token
 * @param {Function} errorFunction - function to execute if an error occures
 * @returns dispatch
 */
export function requestExtendPurchaseExpiration(token, errorFunction) {
  return (dispatch) => {
    dispatch(updatePurchase(true));
    purchaseApi
      .increaseExpiration(token)
      .then((purchase) => {
        if (!Object.keys(purchase).length) throw new Error();

        const {
          extendedAt,
          expiresAt,
          currentTime,
          incomingTicketRequestId,
          outgoingTicketRequestId,
          type,
        } = camelizeKeys(purchase);
        if (type !== 'ticket_request') {
          dispatch(setPurchaseExtendedExpiration(extendedAt, expiresAt, currentTime));
          dispatch(updatePurchase(false));
        } else {
          const dataToPoll = [{ id: outgoingTicketRequestId }];
          if (incomingTicketRequestId) dataToPoll.push({ id: incomingTicketRequestId });
          const polls = pollingPromises({
            data: dataToPoll,
            name: 'onReceiveTickets',
            create: (polling, payload) => purchaseApi.startTicketsPoll(token, payload, polling),
          });
          Promise.all(polls)
            .then((polls) => {
              polls.forEach(({ status }) => {
                checkIfRetriesExceeded(status);
              });
              dispatch(getPurchase(token));
            })
            .catch((reason) => {
              dispatch(lockTicketsPollingError(reason));
              dispatch(updatePurchase(false));
            });
        }
      })
      .catch(() => {
        dispatch(setError(205, 'error_when_extending_purchase', 'error', false));
        dispatch(updatePurchase(false));
        if (errorFunction) errorFunction();
      });
  };
}

/** Error handle of membership discount request */
function membershipDiscountError(errorCode, walletName, dispatch) {
  if (errorCode === 401 && (walletName === 'travelpass' || walletName === 'costapass')) {
    triggerSignOutWidget(walletName);
    dispatch(setCostaPassError(errorCode));
  } else {
    dispatch(setError(205, `error_when_using_wallet_${walletName}`, 'error', false));
  }
}

/** Method to apply a loyalty membership discount */
export function membershipDiscountPayment(token, discount, walletName) {
  return (dispatch) => {
    // The user token is set in the requests
    setUserTokenForAPI();
    dispatch(toggleUsingWallet(true));

    purchaseApi
      .membershipDiscountPayment(token, discount)
      .then(() => {
        dispatch(getPurchase(token));
        dispatch(setError(200, `buy_using_your_points_${walletName}`, 'success', false));
      })
      .catch((error) => {
        const { code } = error;
        membershipDiscountError(code, walletName, dispatch);
      })
      .finally(() => {
        dispatch(toggleUsingWallet(false));
      });
  };
}

/** Method to delete the membership discount applied */
export function membershipDiscountPaymentDelete(token, walletName) {
  return (dispatch) => {
    // The user token is set in the requests
    setUserTokenForAPI();
    dispatch(toggleUsingWallet(true));

    purchaseApi
      .membershipDiscountPaymentDelete(token)
      .then(() => {
        dispatch(setError(205, `buy_without_using_wallet_${walletName}`, 'warning', false));
        dispatch(getPurchase(token));
      })
      .catch((error) => {
        const { code } = error;
        membershipDiscountError(code, walletName, dispatch);
      })
      .finally(() => {
        dispatch(toggleUsingWallet(false));
      });
  };
}

/** Method to apply a loyalty membership discount
 * @param {Boolean} updating - boolean to indicate if the purchase is updating
 */
export function toggleFlatFare({ updating }) {
  return { type: types.TOGGLE_FLAT_FARE, updating };
}

/**
 * toggleWantsFlatFare action. Apply or remove a flat fare loyalty discount on a purchase.
 * @param {object} params - token, wantsFlatFare.
 * @param {string} params.token - Purchase token.
 * @param {boolean} params.wantsFlatFare - Whether the user wants the flat fare applied.
 */
export function toggleWantsFlatFare({ token, wantsFlatFare }) {
  return (dispatch) => {
    dispatch(toggleFlatFare({ updating: true }));

    return purchaseApi
      .update(token, { wants_flat_fare: wantsFlatFare })
      .then((purchase) => {
        dispatch(receivePurchase(purchase));
      })
      .catch((reason) => {
        dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
        console.error(reason);
      })
      .finally(() => {
        dispatch(toggleFlatFare({ updating: false }));
      });
  };
}

/** Creates a Yuno checkout session
 * @param {string} purchaseUrl - Base URL for the purchase API
 * @param {string} purchaseToken - Token identifying the purchase
 * @param {number} amount - Payment amount
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<{checkoutSession: string, merchantOrderId: string}>} Checkout session details
 * @throws {Error} If session creation fails or response is invalid
 */
const createYunoCheckoutSession = async (purchaseUrl, purchaseToken, amount, dispatch) => {
  try {
    yuno.initialize(purchaseUrl);
    const sessionData = await yuno.createCheckoutSession({
      purchaseToken,
      amount,
    });
    return sessionData;
  } catch (error) {
    dispatch(setError(205, 'error_creating_yuno_session', 'error', false));
    throw new Error(`Error in createYunoCheckoutSession: ${error.message}`);
  }
};

/** Retrieves session data for a Yuno checkout
 * @param {string} purchaseUrl - Base URL for the purchase API
 * @param {string} checkoutSession - Yuno checkout session identifier
 * @param {string} purchaseToken - Token identifying the purchase
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Available payment methods and session data
 * @throws {Error} If data retrieval fails or response is invalid
 */
const getYunoSessionData = async (purchaseUrl, checkoutSession, purchaseToken, dispatch) => {
  try {
    yuno.initialize(purchaseUrl);
    const { availableMethods } = await yuno.getSessionData({
      checkoutSession,
      purchaseToken,
    });
    return availableMethods;
  } catch (error) {
    dispatch(setError(205, 'error_getting_yuno_session_data', 'error', false));
    throw new Error(`Error in getYunoSessionData: ${error.message}`);
  }
};

/** Fetches Yuno payment integration data
 * @param {object} params - Parameters for fetching Yuno data
 * @param {string} params.purchaseUrl - Base URL for the purchase API
 * @param {string} params.purchaseToken - Token identifying the purchase
 * @param {number} params.amount - Payment amount
 * @returns {Function} Redux thunk action creator
 */
export function fetchYunoData({ purchaseUrl, purchaseToken, amount }) {
  return async (dispatch) => {
    try {
      const { checkoutSession, merchantOrderId } = await createYunoCheckoutSession(
        purchaseUrl,
        purchaseToken,
        amount,
        dispatch,
      );
      const availableMethods = await getYunoSessionData(
        purchaseUrl,
        checkoutSession,
        purchaseToken,
        dispatch,
      );
      const yunoSessionData = {
        checkoutSession,
        merchantOrderId,
        availableMethods,
      };
      dispatch({ type: types.SET_YUNO_SESSION_DATA, yunoSessionData });
    } catch (error) {
      dispatch(setError(205, 'error_fetching_yuno_data', 'error', false));
      throw new Error(`Error on paymentPayload for yunoPaymentAttempt: ${error.message}`);
    }
  };
}

/**
 * Sync seats from seatsBus to purchase state
 * @param {string} departureId - Departure ID
 * @param {string} returnId - Return ID
 * @returns {Function} Dispatch action
 */
export function syncSeatsFromBus(departureId, returnId) {
  return (dispatch, getState) => {
    const {
      seatsBus: seatsBusState,
      whitelabelConfig: { features },
    } = getState();

    const { USE_SEAT_BASED_PRICING: useSeatBasedPricing } = features;
    if (!useSeatBasedPricing) return;
    const seatsBus = seatsBusState.toJS();

    if (departureId && seatsBus[departureId]?.selectedSeats?.length) {
      dispatch(selectSeats('departs', seatsBus[departureId].selectedSeats));
    }
    if (returnId && seatsBus[returnId]?.selectedSeats?.length) {
      dispatch(selectSeats('returns', seatsBus[returnId].selectedSeats));
    }
  };
}

/**
 * Request a promotional code
 * @returns {Object} Dispatch action
 */
export function requestPromotionalCode() {
  return { type: types.REQUEST_PROMO_CODE };
}

/**
 * Receive a promotional code
 * @param {boolean} success - Whether the promotional code was successfully applied
 * @returns {Object} Dispatch action
 */
export function receivePromotionalCode(success) {
  return { type: types.RECEIVE_PROMO_CODE, success };
}

/**
 * Apply a promotional code to the purchase
 * @param {string} token - Purchase token
 * @param {string} code - Promotional code
 * @returns {Promise} Promise that resolves with the result
 */
export function applyPromotionalCode(token, code) {
  return (dispatch) => {
    dispatch(requestPromotionalCode());
    dispatch({ type: types.PROMO_CODE_SUBMITTING, isSubmitting: true });

    return purchaseApi
      .validatePromotionalCode(token, code)
      .then((payload) => {
        const { success } = payload;
        dispatch(receivePromotionalCode(success));

        if (!success) {
          dispatch(setError(206, 'invalid_promo_code', 'error', false, payload.message));
        }

        return { success, message: payload.message };
      })
      .catch((reason) => {
        dispatch(receivePromotionalCode(false));

        dispatch(
          setError(
            206,
            reason.code === 500 ? 'error_when_applying_promo_code' : 'invalid_promo_code',
            'error',
            false,
          ),
        );
        throw reason;
      })
      .finally(() => {
        dispatch({ type: types.PROMO_CODE_SUBMITTING, isSubmitting: false });
      });
  };
}
